import { computed, isRef, type MaybeRef } from 'vue'
import type { Policy } from '@/modules/groups/interfaces/policy'
import type { Rule } from '@/interfaces/rule'
import type { PolicyObjectMap } from '@/interfaces/PolicyObjectMap'

export interface PolicyContext {
	totalFare?: number
	nights?: number
	daysInAdvance?: number
	dailyRate?: number
}

export interface LimitInfo {
	min?: number
	max?: number
	unit: string
}

export interface PolicyEvaluation {
	policy: Policy
	policyName: string
	violated: boolean
	currentValue: number | null
	limitInfo: LimitInfo
	label: string
}

const POLICY_LABELS: Record<string, string> = {
	totalStay: 'Total de estancia',
	nightAmount: 'Máximo de noches',
	daysInAdvanceRange: 'Anticipación de reserva',
	dailyRateRange: 'Tarifa por noche',
}

// ── Paridad EXACTA con el backend (W2) ───────────────────────────────────────
// Este banner es informativo; el enforcement real es User::checkPolicies. Si el
// cliente evalúa distinto, miente justo en los bordes. Semántica portada:
//   value → violado si !doComparison(v, operator, value)  (operador configurable)
//   range → violado si !(min <= v+1 && max >= v+1)        (el +1 heredado del backend)
// Si el backend cambia (p. ej. quita el +1), cambiar AQUÍ — única fuente de deriva.

const complies = (value: number, operator: string | undefined, limit: number): boolean => {
	switch (operator) {
		case 'eq': return value === limit
		case 'ne': return value !== limit
		case 'lt': return value < limit
		case 'lte': return value <= limit
		case 'gt': return value > limit
		case 'gte': return value >= limit
		default: return value <= limit // las políticas value del seeder usan lte
	}
}

const rangeViolated = (current: number, min?: number, max?: number): boolean => {
	const shifted = current + 1
	return (min !== undefined && shifted < min) || (max !== undefined && shifted > max)
}

// Eje de contexto por nombre de política: qué valor comparar, la unidad para el
// mensaje, y si la política es de rango (min/max) o de valor (operator/value).
type Axis = { value: number | undefined; unit: string; kind: 'value' | 'range' }

const axisFor = (name: string, ctx: PolicyContext): Axis | null => {
	switch (name) {
		case 'totalStay': return { value: ctx.totalFare, unit: 'MXN', kind: 'value' }
		case 'nightAmount': return { value: ctx.nights, unit: 'noches', kind: 'value' }
		case 'daysInAdvanceRange': return { value: ctx.daysInAdvance, unit: 'días', kind: 'range' }
		case 'dailyRateRange': return { value: ctx.dailyRate, unit: 'MXN/noche', kind: 'range' }
		default: return null
	}
}

const ruleViolated = (kind: 'value' | 'range', rule: Rule, current: number): boolean => {
	if (kind === 'range') {
		return rangeViolated(
			current,
			rule.min ? Number(rule.min) : undefined,
			rule.max ? Number(rule.max) : undefined,
		)
	}

	return ! complies(current, rule.operator, Number(rule.value))
}

const evaluateSingle = (
	name: string,
	policy: Policy,
	ctx: PolicyContext,
): PolicyEvaluation | null => {
	const rules = policy.rule
	if (!rules || rules.length === 0) return null

	const axis = axisFor(name, ctx)
	if (!axis || axis.value === undefined) return null

	const current = axis.value

	// Paridad con el backend (User::checkPolicies itera foreach sobre pivot->rule):
	// se evalúan TODAS las reglas; la política se marca violada si CUALQUIERA viola.
	// Antes solo rule[0] → contradecía el enforcement server-side multi-regla.
	const violated = rules.some((rule) => ruleViolated(axis.kind, rule, current))

	// limitInfo para el mensaje se toma de la primera regla (caso común: 1 regla).
	// El flag `violated` de arriba ya considera todas.
	const first = rules[0]
	const limitInfo: LimitInfo = axis.kind === 'range'
		? {
			min: first.min ? Number(first.min) : undefined,
			max: first.max ? Number(first.max) : undefined,
			unit: axis.unit,
		}
		: { max: Number(first.value), unit: axis.unit }

	return {
		policy,
		policyName: name,
		label: POLICY_LABELS[name] ?? policy.displayName,
		violated,
		currentValue: current,
		limitInfo,
	}
}

const usePolicyEvaluator = (
	policyObjectMap: MaybeRef<PolicyObjectMap | null>,
	context: MaybeRef<PolicyContext>,
) => {
	const map = isRef(policyObjectMap) ? policyObjectMap : computed(() => policyObjectMap as PolicyObjectMap | null)
	const ctx = isRef(context) ? context : computed(() => context as PolicyContext)

	const evaluations = computed((): PolicyEvaluation[] => {
		if (!map.value) return []
		return Object.entries(map.value)
			.map(([name, policy]) => policy ? evaluateSingle(name, policy, ctx.value) : null)
			.filter((e): e is PolicyEvaluation => e !== null)
	})

	const violations = computed(() => evaluations.value.filter(e => e.violated))
	const hardViolations = computed(() => violations.value.filter(e => !e.policy.isFlexible))
	const softViolations = computed(() => violations.value.filter(e => e.policy.isFlexible))
	const compliant = computed(() => evaluations.value.filter(e => !e.violated))
	const hasViolations = computed(() => violations.value.length > 0)
	const hasHardViolations = computed(() => hardViolations.value.length > 0)

	return {
		evaluations,
		violations,
		hardViolations,
		softViolations,
		compliant,
		hasViolations,
		hasHardViolations,
	}
}

export default usePolicyEvaluator
